import React from 'react';

import { useCameraStreamStart } from 'src/features/camera/api/useCameraStreamStart';
import {
    ICE_SERVERS,
    STREAM_RESTART_DELAY_MS,
    waitForIceGatheringComplete,
} from 'src/features/videoPlayer/utils/webrtc';
import Video from 'src/features/videoPlayer/Video';
import { useParking } from 'src/providers/ParkingProvider';

type Props = {
    selectedGateway: string;
    loading: boolean;
    setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setError: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function WebRTCVideoPlayer(props: Props) {
    const { selectedGateway, setIsPlaying, loading, setLoading, setError } = props;
    const { parkingId } = useParking();

    const videoRef = React.useRef<HTMLVideoElement>(null);
    const { mutateAsync: startCameraWebRTC } = useCameraStreamStart({
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            setError(true);
        },
    });

    React.useEffect(() => {
        // Guards every async continuation below: the effect can be torn down mid-handshake.
        let stopped = false;
        let connection: RTCPeerConnection | null = null;
        let restartTimer: ReturnType<typeof setTimeout> | undefined;

        const detachVideo = () => {
            if (!videoRef.current) return;

            videoRef.current.pause();
            videoRef.current.srcObject = null;
        };

        const teardown = () => {
            if (restartTimer) clearTimeout(restartTimer);
            restartTimer = undefined;

            connection?.close();
            connection = null;
            detachVideo();
        };

        /** Rebuild the peer connection after it drops, rather than leaving a frozen <video>. */
        const restart = () => {
            if (stopped) return;

            teardown();
            setIsPlaying(false);
            setLoading(true);
            restartTimer = setTimeout(start, STREAM_RESTART_DELAY_MS);
        };

        const negotiate = async (webrtc: RTCPeerConnection) => {
            // We only consume the camera; go2rtc has nothing to receive from us.
            webrtc.addTransceiver('video', { direction: 'recvonly' });

            await webrtc.setLocalDescription(await webrtc.createOffer());
            await waitForIceGatheringComplete(webrtc);
            if (stopped || webrtc !== connection) return;

            const response = await startCameraWebRTC({
                gatewayId: selectedGateway,
                parkingId: parkingId,
                sdp: webrtc.localDescription?.sdp ?? '',
            });
            if (stopped || webrtc !== connection) return;

            await webrtc.setRemoteDescription({ type: 'answer', sdp: response.sdp });
        };

        function start() {
            if (stopped) return;

            const webrtc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
            connection = webrtc;

            webrtc.ontrack = (event) => {
                if (!videoRef.current || webrtc !== connection) return;

                // go2rtc does not always advertise a stream id, so fall back to the bare track.
                videoRef.current.srcObject = event.streams[0] ?? new MediaStream([event.track]);
                videoRef.current
                    .play()
                    .then(() => {
                        setLoading(false);
                        setIsPlaying(true);
                    })
                    .catch((error) => {
                        console.warn('play error', error);
                    });
            };

            // Connection state replaces the keepalive data channel the previous streaming server
            // needed: go2rtc reports liveness through the peer connection itself.
            webrtc.onconnectionstatechange = () => {
                if (webrtc !== connection) return;

                if (webrtc.connectionState === 'connected') {
                    setIsPlaying(true);
                } else if (webrtc.connectionState === 'failed' || webrtc.connectionState === 'disconnected') {
                    restart();
                }
            };

            negotiate(webrtc).catch(() => {
                if (stopped) return;

                setLoading(false);
                setError(true);
            });
        }

        start();

        return () => {
            stopped = true;
            teardown();
            setIsPlaying(false);
            setLoading(true);
        };
    }, [selectedGateway, startCameraWebRTC, setIsPlaying, setLoading, setError, parkingId]);

    return <Video loading={loading} videoRef={videoRef} />;
}
